import { CountryCode } from "DataMapper/CountryCode"
import { ChangeEvent, FunctionComponent, useEffect, useRef } from "react"
import { trackClick } from "scripts/segment"
import { pageName } from "utils/events-utils"
import { PrimaryButton, ImageButton } from "components/Inputs/Button"
import { validatePhoneNumberOnCountry } from "DataMapper/CountryCode"
import CountryCodeSelector from "components/Login/CountryCodeSelector"
import { isMobile } from "react-device-detect"

interface Props {
  phone: string
  phoneText: string
  phoneErrMsg: string
  showTruecaller: boolean
  phoneInfoMsg: string
  country: CountryCode
  canChangeCountryCode: boolean
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void
  changeView: (view: string) => void
  generateOtp: () => void
  truecallerLoginManager: () => void
  changeCountry: (country: CountryCode) => void
  isLoading: boolean
  isGeneratingOtp?: boolean
}

const EnterMobile: FunctionComponent<Props> = ({
  phone,
  phoneText,
  phoneErrMsg,
  showTruecaller,
  phoneInfoMsg,
  country,
  canChangeCountryCode,
  handleChange,
  generateOtp,
  truecallerLoginManager,
  changeCountry,
  isLoading,
  isGeneratingOtp,
}) => {
  const hadSentEvent = useRef(false)
  const phoneRef = useRef<HTMLInputElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleInputFocus = () => {
    if (isMobile && phoneRef.current) {
      timeoutRef.current = setTimeout(() => {
        phoneRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 300)
    }
  }

  const handlePhoneChange = (e: any) => {
    handleChange(e)
  }

  const preventNumberInputScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur()
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="mb-6 md:mb-[40px] w-full">
      <div
        className={`relative flex items-center w-full rounded-[8px] border border-solid ${
          validatePhoneNumberOnCountry(phone, country)
            ? "border-[rgba(74,172,100,1)]"
            : "border-[#dbdbdb]"
        } bg-white transition-[border-color] duration-200 ease h-[48px] p-[12px_16px] text-[#1c2233] focus-within:border-[#6555db] focus-within:border-[1px] focus-within:shadow-[0_0_0_2px_rgba(90,77,194,0.1)]`}
      >
        <div
          className={`md:text-lg text-base ${
            canChangeCountryCode
              ? "border-r border-[#dbdbdb] pr-3 mr-3"
              : "pl-4 pr-2 border-r border-gray-300"
          }`}
        >
          {canChangeCountryCode ? (
            <CountryCodeSelector
              country={country}
              changeCountry={changeCountry}
            />
          ) : (
            "+91"
          )}
        </div>

        <input
          ref={phoneRef}
          className={`text-[16px] py-[14px] pr-[16px] bg-transparent text-[#1c1c1c] font-[400] caret-[#443eff] flex-1 h-full transition-all duration-200 ease placeholder:text-[#686868] placeholder:opacity-70 placeholder:transition-opacity placeholder:duration-200 focus:placeholder:opacity-50 md:text-[16px] md:leading-[1.5rem] border-0 ${
            canChangeCountryCode ? "w-[60%] md:w-[66.666667%]" : ""
          }`}
          type="number"
          inputMode="numeric"
          value={phone}
          onChange={handlePhoneChange}
          placeholder="Mobile Number"
          onFocus={handleInputFocus}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            // Prevent 'e' and 'E' characters
            if (e.key === "e" || e.key === "E") {
              e.preventDefault()
            }

            if (e.key === "Enter") {
              generateOtp()
            } else {
              if (!hadSentEvent.current) {
                trackClick(pageName.Login_Modal, {
                  widgetName: "Login Modal",
                  widgetFormat: "Modal",
                  contentName: "Input Number",
                  contentFormat: "Button",
                })
                hadSentEvent.current = true
              }
            }
          }}
          onWheel={preventNumberInputScroll}
          autoFocus={!isMobile}
        />
      </div>

      {!phoneErrMsg && phoneInfoMsg && (
        <p className="text-[0.875rem] leading-[1.25rem] text-center pt-[0.5rem] w-full text-[rgba(68,62,255,1)]">
          {phoneInfoMsg}
        </p>
      )}
      {phoneErrMsg && !validatePhoneNumberOnCountry(phone, country) && (
        <p className="text-[0.875rem] leading-[1.25rem] text-center pt-[0.5rem] w-full text-[rgba(255,105,91,1)]">
          {phoneErrMsg}
        </p>
      )}

      <PrimaryButton
        py="pt-[14px] pb-[14px] md:py-3"
        disabled={
          !validatePhoneNumberOnCountry(phone, country) ||
          isLoading ||
          isGeneratingOtp
        }
        className="mt-4 mb-4 w-full rounded-[12px] shadow-[0px_3px_10px_-2px_rgba(28,34,51,0.12)] !important"
        ctaText={phoneText}
        onClick={generateOtp}
      />

      {isMobile && showTruecaller && (
        <>
          <div className="w-full text-[#888] font-[400] text-center text-[16px] my-[4px] relative flex items-center justify-center before:content-[''] before:flex-1 before:h-[1px] before:bg-[#e0e0e0] before:mx-[15px] after:content-[''] after:flex-1 after:h-[1px] after:bg-[#e0e0e0] after:mx-[15px]">
            or
          </div>
          <button
            className="flex items-center justify-center font-bold text-base w-full py-3 px-4 rounded-lg border border-[#0086FE] text-[#0086FE] bg-white cursor-pointer transition-all duration-200 mt-4 shadow-sm hover:bg-[rgba(59,130,246,0.05)] md:mt-5 md:mb-5"
            onClick={truecallerLoginManager}
          >
            Continue with
            <img
              src={"/assets/truecaller-logo.svg"}
              alt="truecaller"
              width={79}
              height={25}
            />
          </button>
        </>
      )}
    </div>
  )
}

export default EnterMobile
